import { useMemo } from 'react';
import { Star, Trophy } from 'lucide-react';
import { computeQuality, qualityBand, qualityColorClasses } from '@/lib/qualityScore';
import CompareThesesButton from '@/components/CompareThesesButton';
import InlinePopover from '@/components/InlinePopover';

/**
 * WatchlistSummaryCard
 * ---------------------
 * At-a-glance dashboard surfaced above the watchlist table. Crunches
 * the user's watchlist tickers (passed in via `stocks` already filtered
 * to watchlist) into 3 useful facts:
 *
 *   1. Avg Quality Score · best/worst tickers (clickable)
 *   2. Yield range (lowest → highest, with tickers)
 *   3. Top sectors as horizontal bars (no extra chart dep)
 *
 * Round 95bc (Jun 2026) — Unusual Activity, Recent Hikes/Cuts, and
 * Ex-Div ≤ 7 Days now live in standalone strips rendered in
 * Dashboard.js. See `UnusualActivityLine`, `RecentDividendEventsLine`,
 * and `ExDividendThisWeekLine`.
 *
 * Auto-hides on empty watchlist (parent renders the empty-state).
 */

function StatBox({ label, children, testId }) {
  return (
    <div className="rounded-lg border bg-card px-3 py-2.5" data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold mb-1.5">
        {label}
      </div>
      {children}
    </div>
  );
}

function ClickableTicker({ ticker, onClick, className = '' }) {
  return (
    <button
      type="button"
      onClick={() => onClick?.(ticker)}
      className={`font-mono text-xs font-bold text-yellow-600 dark:text-yellow-400 hover:underline ${className}`}
      data-testid={`watchlist-summary-ticker-${ticker}`}
    >
      {ticker}
    </button>
  );
}

export default function WatchlistSummaryCard({ stocks = [], onStockClick, onCompareClick }) {
  const summary = useMemo(() => {
    if (!stocks || stocks.length === 0) return null;

    // Quality score stats — DIVIDEND tickers only (dividend triangle).
    const divStocks = stocks.filter(s => s?.pays_dividend !== false);
    const nonDivStocks = stocks.filter(s => s?.pays_dividend === false);

    const qualities = divStocks.map(s => ({ ticker: s.ticker, q: computeQuality(s)?.score })).filter(x => x.q != null);
    const avgQuality = qualities.length
      ? Math.round(qualities.reduce((a, b) => a + b.q, 0) / qualities.length)
      : null;
    const sortedByQ = [...qualities].sort((a, b) => b.q - a.q);
    const best = sortedByQ[0] || null;
    const worst = sortedByQ[sortedByQ.length - 1] || null;

    // Round 97d (Feb 27, 2026) — Non-div quality stats. Mirrors the
    // dividend side but uses the persisted Non-Dividend Quality Score
    // + tier instead of the dividend triangle.
    const ndQualities = nonDivStocks
      .map(s => ({
        ticker: s.ticker,
        q: Number.isFinite(Number(s.non_dividend_quality_score)) ? Number(s.non_dividend_quality_score) : null,
        tier: s.non_dividend_quality_tier || null,
      }))
      .filter(x => x.q != null);
    const ndAvgQuality = ndQualities.length
      ? Math.round(ndQualities.reduce((a, b) => a + b.q, 0) / ndQualities.length)
      : null;
    const ndSorted = [...ndQualities].sort((a, b) => b.q - a.q);
    const ndBest = ndSorted[0] || null;
    const ndWorst = ndSorted[ndSorted.length - 1] || null;

    // Yield stats — DIVIDEND side
    const yields = divStocks
      .filter(s => Number.isFinite(Number(s.dividend_yield)) && Number(s.dividend_yield) > 0)
      .map(s => ({ ticker: s.ticker, y: Number(s.dividend_yield) }));
    const sortedByY = [...yields].sort((a, b) => a.y - b.y);
    const lowY = sortedByY[0] || null;
    const highY = sortedByY[sortedByY.length - 1] || null;

    // Round 97d (Feb 27, 2026) — Revenue growth range for the non-div
    // half of the box. Uses revenue_3yr_cagr from the scorer (fraction,
    // e.g. 0.18 = 18%). Low → high shows the worst-grower → best-
    // grower in the watchlist.
    const ndGrowths = nonDivStocks
      .map(s => ({
        ticker: s.ticker,
        g: (s.non_dividend_quality_signals || {}).revenue_3yr_cagr,
      }))
      .filter(x => Number.isFinite(Number(x.g)))
      .map(x => ({ ticker: x.ticker, g: Number(x.g) }));
    const sortedByG = [...ndGrowths].sort((a, b) => a.g - b.g);
    const lowG = sortedByG[0] || null;
    const highG = sortedByG[sortedByG.length - 1] || null;

    // Sector mix (top 3)
    const sectorCounts = {};
    for (const s of stocks) {
      const sec = (s.sector || '').trim();
      if (!sec) continue;
      sectorCounts[sec] = (sectorCounts[sec] || 0) + 1;
    }
    const totalWithSector = Object.values(sectorCounts).reduce((a, b) => a + b, 0) || 1;
    const topSectors = Object.entries(sectorCounts)
      .sort(([, a], [, b]) => b - a)
      .slice(0, 4)
      .map(([name, count]) => ({ name, count, pct: (count / totalWithSector) * 100 }));

    // Recent dividend hikes & cuts
    // Recent dividend hikes & cuts (computed here for backward-compat;
    // the standalone RecentDividendEventsLine in Dashboard renders the
    // user-visible strips).
    const hikes = stocks
      .filter(s => s.recent_div_change_type === 'raise' && Number.isFinite(Number(s.recent_div_change_pct)))
      .sort((a, b) => Number(b.recent_div_change_pct) - Number(a.recent_div_change_pct))
      .slice(0, 5);
    const cuts = stocks
      .filter(s => s.recent_div_change_type === 'cut' && Number.isFinite(Number(s.recent_div_change_pct)))
      .sort((a, b) => Number(a.recent_div_change_pct) - Number(b.recent_div_change_pct))
      .slice(0, 5);

    return {
      avgQuality, best, worst,
      ndAvgQuality, ndBest, ndWorst,
      lowY, highY,
      lowG, highG,
      topSectors, hikes, cuts, count: stocks.length,
      divCount: divStocks.length, nonDivCount: nonDivStocks.length,
      // Round 97d3 (Feb 27, 2026) — Composite Avg Conviction across
      // BOTH div + non-div cohorts. Single big-number read for users
      // who don't want to mentally average two separate boxes.
      // Unscored tickers (no dividend quality possible AND no non-div
      // tier) are excluded from the average so they don't drag the
      // number down with a falsy 0.
      combinedQualityScores: [
        ...qualities.map(q => q.q),
        ...ndQualities.map(q => q.q),
      ],
    };
  }, [stocks]);

  if (!summary || summary.count === 0) return null;

  const {
    avgQuality, best, worst,
    ndAvgQuality, ndBest, ndWorst,
    lowY, highY,
    lowG, highG,
    topSectors, count,
    divCount, nonDivCount,
    combinedQualityScores,
  } = summary;

  const combinedAvg = combinedQualityScores.length
    ? Math.round(combinedQualityScores.reduce((a, b) => a + b, 0) / combinedQualityScores.length)
    : null;
  const combinedAnalyzed = combinedQualityScores.length;
  const combinedBand = combinedAvg != null ? qualityBand(combinedAvg) : null;
  const combinedCls = combinedBand ? qualityColorClasses(combinedBand.tone) : null;
  const avgBand = qualityBand(avgQuality);
  const avgCls = qualityColorClasses(avgBand.tone);

  return (
    <div className="rounded-xl border bg-card/50 p-3 sm:p-4 mb-4" data-testid="watchlist-summary-card">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Star className="w-4 h-4 fill-yellow-500 text-yellow-500" />
          <h3 className="text-sm font-bold uppercase tracking-wider">Watchlist Snapshot</h3>
          <span className="text-[10px] text-muted-foreground font-mono">({count} {count === 1 ? 'stock' : 'stocks'})</span>
        </div>
        {onCompareClick && count >= 2 && (
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={onCompareClick}
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-bold bg-fuchsia-100 dark:bg-fuchsia-950/40 text-fuchsia-700 dark:text-fuchsia-300 hover:bg-fuchsia-200 dark:hover:bg-fuchsia-950/60 border border-fuchsia-300/60 shadow-sm hover:shadow active:translate-y-px transition-all"
              data-testid="watchlist-compare-quality-btn"
              title="Open a side-by-side breakdown of every watchlist ticker's Quality pillars."
            >
              <Trophy className="w-3.5 h-3.5" />
              <span className="text-[10px] font-bold uppercase tracking-wider">Compare Quality</span>
            </button>
            <CompareThesesButton
              label="Compare Theses"
              size="sm"
              testId="watchlist-compare-theses-btn"
            />
          </div>
        )}
      </div>

      {/* Round 97d3 (Feb 27, 2026) — Avg Conviction composite tile.
          Sits above the per-cohort grid as a single at-a-glance read
          of the whole watchlist's quality, regardless of stock type.
          Hidden when nothing is scored yet so we don't show a
          confusing "—". */}
      {combinedAvg != null && (
        <div className="rounded-lg border bg-card px-3 py-2.5 mb-3 flex items-center justify-between gap-3 flex-wrap" data-testid="ws-avg-conviction">
          <div className="flex items-center gap-3 flex-wrap">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">Avg Conviction</div>
              <div className="flex items-baseline gap-1.5">
                <span className={`font-mono font-extrabold text-2xl leading-none ${combinedCls.text}`}>
                  {combinedAvg}
                </span>
                <span className="text-xs text-muted-foreground">/ 100</span>
                {/* Round 96f (Jul 2026) — Tier pill now carries a
                    plain-language tooltip so users hovering (desktop)
                    or long-tapping (mobile) the letter grade get an
                    explanation of what D / B / ND means. `title`
                    doubles as the native mobile long-press tooltip. */}
                <InlinePopover
                  className={`inline-flex items-center gap-1 font-bold uppercase tracking-wider ring-1 rounded-full px-1.5 py-0.5 text-[10px] ml-1 cursor-help ${combinedCls.bg} ${combinedCls.text} ${combinedCls.ring}`}
                  title={`Grade "${combinedBand.short}" — ${combinedBand.label}. Composite conviction band derived from the score ${combinedAvg}/100. A+ Elite (85+), A Strong (70-84), B Healthy (55-69), C Average (40-54), D Weak (25-39), F Risky (<25).`}
                  testId="ws-avg-conviction-tier-pill"
                  maxWidth={280}
                  render={() => (
                    <div className="text-left text-[11px] leading-snug text-foreground/90">
                      <p className="font-bold mb-1">Grade &ldquo;{combinedBand.short}&rdquo; — {combinedBand.label}</p>
                      <p>Composite conviction band derived from the score {combinedAvg}/100 across every scored ticker (dividend + non-dividend) in this watchlist.</p>
                      <p className="mt-1.5 font-mono text-[10px] text-muted-foreground">A+ Elite (85+) · A Strong (70–84) · B Healthy (55–69) · C Average (40–54) · D Weak (25–39) · F Risky (&lt;25)</p>
                    </div>
                  )}
                >
                  <span className={`rounded-full w-1.5 h-1.5 ${combinedCls.dot}`} />
                  {combinedBand.short}
                </InlinePopover>
              </div>
            </div>
            {/* Round 96f (Jul 2026) — "Weak" (or whichever band label
                the current score maps to) now inherits the same
                colour as the composite score number so the two read
                as one unit at a glance. */}
            <span className={`text-[11px] font-semibold ${combinedCls.text}`}>{combinedBand.label}</span>
          </div>
          <div className="text-[10px] text-muted-foreground/90 leading-relaxed">
            <span className="font-mono font-bold text-foreground">{combinedAnalyzed}</span> of {count} analyzed
            <br />
            <span className="opacity-80">
              {divCount > 0 && <>{divCount} div</>}
              {divCount > 0 && nonDivCount > 0 && ' · '}
              {nonDivCount > 0 && <>{nonDivCount} non-div</>}
              {count - combinedAnalyzed > 0 && <> · <span className="text-amber-600 dark:text-amber-400">{count - combinedAnalyzed} unscored</span></>}
            </span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {/* Quality — split: dividend triangle (left) | non-div tier (right).
            Round 97d (Feb 27, 2026). Each half renders only if its
            cohort has at least one ticker. */}
        <StatBox label={nonDivCount > 0 && divCount > 0 ? 'Avg Quality · Div / Non-Div' : 'Avg Quality'} testId="ws-avg-quality">
          <div className={`grid ${nonDivCount > 0 && divCount > 0 ? 'grid-cols-2 gap-2 divide-x divide-border/40' : ''}`}>
            <div className={nonDivCount > 0 && divCount > 0 ? 'pr-2' : ''}>
              {avgQuality != null ? (
                <>
                  <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                    <InlinePopover
                      className={`inline-flex items-center font-bold uppercase tracking-wider ring-1 rounded-full px-1.5 py-0.5 text-[11px] gap-1 cursor-help ${avgCls.bg} ${avgCls.text} ${avgCls.ring}`}
                      title={`Dividend Quality avg = ${avgQuality}/100 → grade "${avgBand.short}" (${avgBand.label}). Blends dividend growth, payout ratio, revenue stability and yield safety across the ${divCount} dividend ticker${divCount === 1 ? '' : 's'} in this watchlist.`}
                      testId="ws-avg-quality-div-pill"
                      maxWidth={280}
                      render={() => (
                        <div className="text-left text-[11px] leading-snug text-foreground/90">
                          <p className="font-bold mb-1">Dividend Quality avg = {avgQuality}/100 → grade &ldquo;{avgBand.short}&rdquo; ({avgBand.label})</p>
                          <p>Blends dividend growth, payout ratio, revenue stability and yield safety across the {divCount} dividend ticker{divCount === 1 ? '' : 's'} in this watchlist.</p>
                          <p className="mt-1.5 font-mono text-[10px] text-muted-foreground">A+ Elite (85+) · A Strong (70–84) · B Healthy (55–69) · C Average (40–54) · D Weak (25–39) · F Risky (&lt;25)</p>
                        </div>
                      )}
                    >
                      <span className={`rounded-full w-2 h-2 ${avgCls.dot}`} />
                      {avgQuality}
                      <span className="opacity-70 font-mono">{avgBand.short}</span>
                    </InlinePopover>
                    <span className="text-[10px] text-muted-foreground">{avgBand.label}</span>
                  </div>
                  {best && worst && best.ticker !== worst.ticker && (
                    <div className="text-[10px] text-muted-foreground space-y-0.5">
                      <div className="flex items-center gap-1">
                        <span className="text-emerald-600 dark:text-emerald-400">↑</span>
                        Best: <ClickableTicker ticker={best.ticker} onClick={onStockClick} /> ({best.q})
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-rose-600 dark:text-rose-400">↓</span>
                        Worst: <ClickableTicker ticker={worst.ticker} onClick={onStockClick} /> ({worst.q})
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <span className="text-xs text-muted-foreground">{divCount === 0 ? 'No div holdings' : '—'}</span>
              )}
            </div>
            {nonDivCount > 0 && divCount > 0 && (
              <div className="pl-2" data-testid="ws-avg-quality-nondiv">
                {ndAvgQuality != null ? (
                  <>
                    <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                      <InlinePopover
                        className="inline-flex items-center gap-1 font-bold uppercase tracking-wider ring-1 ring-violet-300/40 dark:ring-violet-700/40 bg-violet-100 dark:bg-violet-950/40 text-violet-800 dark:text-violet-200 rounded-full px-1.5 py-0.5 text-[11px] cursor-help"
                        title={`Non-Dividend Quality avg = ${ndAvgQuality}/100. "ND" = Non-Dividend cohort. Score blends revenue growth, gross margin, insider ownership, forward-looking earnings and price momentum across the ${nonDivCount} non-dividend ticker${nonDivCount === 1 ? '' : 's'} in this watchlist.`}
                        testId="ws-avg-quality-nondiv-pill"
                        maxWidth={280}
                        render={() => (
                          <div className="text-left text-[11px] leading-snug text-foreground/90">
                            <p className="font-bold mb-1">Non-Dividend Quality avg = {ndAvgQuality}/100</p>
                            <p>&ldquo;ND&rdquo; = Non-Dividend cohort. Score blends revenue growth, gross margin, insider ownership, forward-looking earnings and price momentum across the {nonDivCount} non-dividend ticker{nonDivCount === 1 ? '' : 's'} in this watchlist.</p>
                          </div>
                        )}
                      >
                        <span className="rounded-full w-2 h-2 bg-violet-500" />
                        {ndAvgQuality}
                        <span className="opacity-70 font-mono">ND</span>
                      </InlinePopover>
                      <span className="text-[10px] text-muted-foreground">Non-Div</span>
                    </div>
                  {/* Round 97d2 (Feb 27, 2026) — Always surface the
                      best/worst non-div tickers (clickable). When
                      only ONE non-div has a tier, show it as a
                      single chip so users still get the ticker name
                      → modal hop instead of an orphan number. */}
                  {ndBest && (ndWorst && ndBest.ticker !== ndWorst.ticker ? (
                    <div className="text-[10px] text-muted-foreground space-y-0.5">
                      <div className="flex items-center gap-1">
                        <span className="text-emerald-600 dark:text-emerald-400">↑</span>
                        Best: <ClickableTicker ticker={ndBest.ticker} onClick={onStockClick} /> ({ndBest.q})
                      </div>
                      <div className="flex items-center gap-1">
                        <span className="text-rose-600 dark:text-rose-400">↓</span>
                        Worst: <ClickableTicker ticker={ndWorst.ticker} onClick={onStockClick} /> ({ndWorst.q})
                      </div>
                    </div>
                  ) : (
                    <div className="text-[10px] text-muted-foreground">
                      <ClickableTicker ticker={ndBest.ticker} onClick={onStockClick} /> ({ndBest.q})
                    </div>
                  ))}
                  </>
                ) : (
                  <span className="text-xs text-muted-foreground">No tier yet</span>
                )}
              </div>
            )}
          </div>
        </StatBox>

        {/* Yield range — split: dividend yield (left) | non-div revenue
            3yr CAGR range (right). Round 97d (Feb 27, 2026). */}
        <StatBox label={nonDivCount > 0 && divCount > 0 ? 'Yield · Rev Growth' : 'Yield Range'} testId="ws-yield-range">
          <div className={`grid ${nonDivCount > 0 && divCount > 0 ? 'grid-cols-2 gap-2 divide-x divide-border/40' : ''}`}>
            <div className={nonDivCount > 0 && divCount > 0 ? 'pr-2' : ''}>
              {lowY && highY ? (
                <>
                  <div className="text-base font-bold font-mono text-green-600 dark:text-green-400">
                    {lowY.y.toFixed(2)}% <span className="text-muted-foreground font-normal mx-1">→</span> {highY.y.toFixed(2)}%
                  </div>
                  {lowY.ticker !== highY.ticker && (
                    <div className="text-[10px] text-muted-foreground mt-0.5">
                      <ClickableTicker ticker={lowY.ticker} onClick={onStockClick} />
                      <span className="mx-1">low ·</span>
                      <ClickableTicker ticker={highY.ticker} onClick={onStockClick} />
                      <span className="ml-1">high</span>
                    </div>
                  )}
                </>
              ) : (
                <span className="text-xs text-muted-foreground">{divCount === 0 ? 'No div holdings' : '—'}</span>
              )}
            </div>
            {nonDivCount > 0 && divCount > 0 && (
              <div className="pl-2" data-testid="ws-yield-range-nondiv">
                {lowG && highG ? (
                  <>
                    <div className="text-base font-bold font-mono text-violet-600 dark:text-violet-400">
                      {(lowG.g * 100).toFixed(1)}% <span className="text-muted-foreground font-normal mx-1">→</span> {(highG.g * 100).toFixed(1)}%
                    </div>
                    {/* Round 97d2 — Always show ticker(s) for the
                        Rev CAGR side too. Single non-div w/ a CAGR
                        renders as a one-chip strip. */}
                    {lowG.ticker !== highG.ticker ? (
                      <div className="text-[10px] text-muted-foreground mt-0.5">
                        <ClickableTicker ticker={lowG.ticker} onClick={onStockClick} />
                        <span className="mx-1">low ·</span>
                        <ClickableTicker ticker={highG.ticker} onClick={onStockClick} />
                        <span className="ml-1">high</span>
                      </div>
                    ) : (
                      <div className="text-[10px] text-muted-foreground mt-0.5">
                        <ClickableTicker ticker={lowG.ticker} onClick={onStockClick} />
                      </div>
                    )}
                    <div className="text-[9px] text-muted-foreground/70 mt-0.5">Rev 3yr CAGR</div>
                  </>
                ) : (
                  <span className="text-xs text-muted-foreground">No CAGR yet</span>
                )}
              </div>
            )}
          </div>
        </StatBox>

        {/* Sector mix */}
        <StatBox label="Top Sectors" testId="ws-top-sectors">
          {topSectors.length > 0 ? (
            <div className="space-y-1">
              {topSectors.map((s) => (
                <div key={s.name} className="flex items-center gap-1.5">
                  <span className="text-[10px] font-medium truncate flex-1" title={s.name}>{s.name}</span>
                  <div className="w-12 h-1.5 rounded-full bg-muted/60 overflow-hidden">
                    <div className="h-full rounded-full bg-primary/70" style={{ width: `${Math.max(8, s.pct)}%` }} />
                  </div>
                  <span className="text-[10px] font-mono text-muted-foreground w-6 text-right">{s.count}</span>
                </div>
              ))}
            </div>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )}
        </StatBox>
      </div>

      {/* Round 95ba (Jun 2026) — Unusual Activity, Recent Hikes /
          Recent Cuts, and (Round 95bc) Ex-Div ≤ 7 Days all moved
          out to standalone strips rendered in Dashboard.js. See
          `UnusualActivityLine`, `RecentDividendEventsLine`, and
          `ExDividendThisWeekLine`. */}
    </div>
  );
}
